import React, { useMemo } from 'react';
import moment from 'moment';
import '../admin/BookingCalendar.scss';

/**
 * Year-at-a-glance month grid (same UX as Booking Calendar / Manage trips).
 * monthCounts: { [1..12]: { total, detail? } }
 */
export default function MonthPickerGrid({
  year,
  onYearChange,
  selectedMonth,
  onMonthSelect,
  monthCounts = {},
  loading = false,
  hint = 'Click a month to see entries.',
}) {
  const currentYear = new Date().getFullYear();

  const months = useMemo(
    () => Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      const stats = monthCounts[month] || { total: 0, detail: '' };
      return {
        month,
        label: moment(`${year}-${String(month).padStart(2, '0')}-01`).format('MMM'),
        total: stats.total || 0,
        detail: stats.detail || '',
      };
    }),
    [year, monthCounts]
  );

  if (loading) {
    return (
      <div className="text-center py-3">
        <div className="spinner-border spinner-border-sm text-primary" role="status">
          <span className="sr-only">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-cal">
      <div className="d-flex justify-content-end mb-2">
        <div className="booking-cal__year-nav">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => onYearChange(year - 1)}
            aria-label="Previous year"
          >
            ‹
          </button>
          <span className="booking-cal__year-label">{year}</span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => onYearChange(year + 1)}
            aria-label="Next year"
          >
            ›
          </button>
        </div>
      </div>

      <div className="booking-cal__grid mb-3" role="list">
        {months.map((m) => {
          const isSelected = selectedMonth === m.month;
          const isCurrentMonth = year === currentYear && m.month === new Date().getMonth() + 1;
          return (
            <button
              key={m.month}
              type="button"
              role="listitem"
              className={`booking-cal__cell${isSelected ? ' booking-cal__cell--selected' : ''}${isCurrentMonth ? ' booking-cal__cell--current' : ''}`}
              onClick={() => onMonthSelect(m.month === selectedMonth ? null : m.month)}
              aria-pressed={isSelected}
            >
              <span className="booking-cal__cell-label">{m.label}</span>
              <span className="booking-cal__month-line-total d-block text-center">{m.total}</span>
              {m.total > 0 && m.detail && (
                <span className="booking-cal__month-line-detail d-block text-center">{m.detail}</span>
              )}
            </button>
          );
        })}
      </div>

      {!selectedMonth && hint && (
        <p className="text-muted text-center small mb-0">{hint}</p>
      )}
    </div>
  );
}

export function buildTransactionMonthCounts(transactions, year) {
  const counts = {};
  for (let m = 1; m <= 12; m += 1) {
    counts[m] = { total: 0, credits: 0, debits: 0 };
  }
  (transactions || []).forEach((tx) => {
    const d = moment(tx.created_at);
    if (!d.isValid() || d.year() !== year) return;
    const month = d.month() + 1;
    counts[month].total += 1;
    if (tx.direction === 'credit') counts[month].credits += 1;
    else counts[month].debits += 1;
  });
  Object.keys(counts).forEach((key) => {
    const m = counts[key];
    const parts = [];
    if (m.credits > 0) parts.push(`${m.credits} credit${m.credits !== 1 ? 's' : ''}`);
    if (m.debits > 0) parts.push(`${m.debits} paid out`);
    m.detail = parts.join(' · ');
  });
  return counts;
}

export function filterTransactionsByMonth(transactions, year, month) {
  if (!month) return [];
  const ym = `${year}-${String(month).padStart(2, '0')}`;
  return (transactions || []).filter((tx) => moment(tx.created_at).format('YYYY-MM') === ym);
}
